import React from 'react';

import { SELECT_PLAN_SECTION_ID } from '@/constants';
import { useAccount, useUserStore } from '@/hooks/useAccount.ts';
import { useAppModals } from '@/hooks/useAppModals';
import { useDevmodeStripeV2Plans } from '@/hooks/useDevmodeStripeV2Plans';
import { webSubscriptionsModule } from '@/modules/subscriptions';
import { webCampaign } from '@/modules/webCampaign';
import { PaymentDescription } from '@/pages/Paywall/components/PaymentDescription/Description';
import { OneDayVIPPaymentDescription } from '@/pages/Paywall/components/PaymentDescription/OneDayVIPPaymentDescription';
import { PromoCode } from '@/pages/Paywall/components/PromoCode';
import { SubscriptionButton } from '@/pages/Paywall/components/SubscriptionButton';
import { OneDayVIPSubscriptionData } from '@/pages/Paywall/components/SubscriptionButton/components/OneDayVIPSubscriptionData/OneDayVIPSubscriptionData';
import {
  SelectPlanListStyles,
  SelectPlanStyles,
  SubscriptionButtonStyles,
} from '@/pages/Paywall/sections/SelectPlan/SelectPlan.styles';

export const DevmodeSelectPlan: React.FC<{
  title: NonNullable<React.ReactNode> | null;
  vipTitle?: string;
  buttonProps?: {
    onClick?: () => void;
    text?: string;
  };
  isPromoCodeVisible?: boolean;
  sectionId?: string;
}> = ({
  title,
  vipTitle,
  buttonProps,
  isPromoCodeVisible = false,
  sectionId = SELECT_PLAN_SECTION_ID,
}) => {
  const { user } = useAccount();
  const userFromStore = useUserStore((store) => store.user);

  const {
    isLoadingPlans,
    monthlyPlan,
    selectedPlan,
    stripePlans,
    checkIsPlanActive,
    billingPortalLink,
  } = useDevmodeStripeV2Plans();

  const { show } = useAppModals('paywall');

  const subscriptionSet = userFromStore?.onboarding_data?.subscription_set;
  const isOneDayVIP = subscriptionSet === 'one_day_vip';

  const shouldRenderDisabledWeekly = webCampaign.setup.hasDisabledWeeklyPlan;

  if (isLoadingPlans) {
    return <SelectPlanStyles id={sectionId}>Loading...</SelectPlanStyles>;
  }

  const weeklySubscription = webSubscriptionsModule.getPlanConfig({
    id: 'pro.1w',
    currency: 'USD',
    label: 'Weekly',
    price: 14.99,
    original_price: 0,
  });

  if (isOneDayVIP) {
    return (
      <SelectPlanStyles id={sectionId}>
        {title && <>{title}</>}
        {isPromoCodeVisible && <PromoCode text={user?.name} />}
        <SelectPlanListStyles>
          {stripePlans.map((subscription) => (
            <OneDayVIPSubscriptionData
              subscription={subscription}
              vipTitle={vipTitle}
              key={subscription.id}
            />
          ))}
        </SelectPlanListStyles>
        <SubscriptionButtonStyles
          type="button"
          onClick={() => {
            if (buttonProps?.onClick) {
              buttonProps.onClick();
            } else {
              show({ stripePlans, billingPortalLink });
            }
          }}
        >
          {buttonProps?.text || 'Become VIP'}
        </SubscriptionButtonStyles>

        {selectedPlan && (
          <OneDayVIPPaymentDescription selectedPlan={selectedPlan} />
        )}
      </SelectPlanStyles>
    );
  }

  return (
    <SelectPlanStyles id={sectionId}>
      {title && <>{title}</>}
      {isPromoCodeVisible && <PromoCode text={user?.name} />}
      <SelectPlanListStyles>
        {shouldRenderDisabledWeekly && (
          <SubscriptionButton
            selected={false}
            renderDiscount={false}
            subscription={{
              label: weeklySubscription.display,
              id: 'pro.1w',
              price: weeklySubscription.basePriceWithoutSale,
              original_price: weeklySubscription.basePrice,
              currency: 'USD',
            }}
            fullDisabled
          />
        )}
        {stripePlans.map((subscription) => (
          <SubscriptionButton
            selected={checkIsPlanActive(subscription.id)}
            subscription={subscription}
            key={subscription.id}
          />
        ))}
      </SelectPlanListStyles>
      <SubscriptionButtonStyles
        type="button"
        onClick={() => {
          if (buttonProps?.onClick) {
            buttonProps.onClick();
          } else {
            show({ stripePlans, billingPortalLink });
          }
        }}
      >
        {buttonProps?.text || 'GET PREMIUM'}
      </SubscriptionButtonStyles>

      <PaymentDescription
        selectedPlan={selectedPlan}
        monthlyPlan={monthlyPlan}
      />
    </SelectPlanStyles>
  );
};
